import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';

import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  open: boolean;
  workerId: number;
  existingCertificateIds?: number[];
  onClose: () => void;
  onBadgeAdded: () => void;
};

interface CatalogCertificate {
  id: number;
  name: string;
  display_name_en: string | null;
  certificate_type: 'BADGE' | 'CERTIFICATE';
}

const errorMessageFrom = (err: any, fallback: string): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length > 0) return errors.join(', ');
  return fallback;
};

const AddWorkerBadgeModal = ({
  open,
  workerId,
  existingCertificateIds = [],
  onClose,
  onBadgeAdded,
}: Props) => {
  const [certificates, setCertificates] = useState<CatalogCertificate[]>([]);
  const [selected, setSelected] = useState<CatalogCertificate | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchCatalog = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosRubyClient.get('/api/admin/certificates', {
          params: { per_page: 100 },
        });
        const all: CatalogCertificate[] = response.data.data || [];
        setCertificates(
          all.filter((cert) => !existingCertificateIds.includes(cert.id)),
        );
      } catch (err) {
        setError(errorMessageFrom(err, 'Failed to load the badge catalog.'));
      } finally {
        setLoading(false);
      }
    };

    fetchCatalog();
    // `existingCertificateIds` is a fresh array each render; keying the fetch
    // on `open` alone avoids an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (!selected) return;

    try {
      setSubmitting(true);
      setError(null);
      await axiosRubyClient.post(
        `/api/admin/workers/${workerId}/certificates`,
        { certificate_id: selected.id },
      );
      setSelected(null);
      onBadgeAdded();
      onClose();
    } catch (err) {
      setError(errorMessageFrom(err, 'Failed to assign the badge.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelected(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={handleClose}>
      <DialogTitle>Assign a badge</DialogTitle>
      <DialogContent>
        <Stack vertical gap={16} style={{ paddingTop: 8 }}>
          {loading ? (
            <Stack justify="center">
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <Autocomplete
              fullWidth
              getOptionLabel={(option) =>
                `${option.display_name_en || option.name}${
                  option.certificate_type === 'BADGE' ? '' : ' (certificate)'
                }`
              }
              options={certificates}
              value={selected}
              renderInput={(params) => (
                <TextField {...params} autoFocus label="Badge" />
              )}
              onChange={(_event, value) => setSelected(value)}
            />
          )}

          <Small color="lighter">
            Granting a badge lets this worker be matched to and claim shifts
            that require it. Only badges can be set as a client requirement.
          </Small>

          {error && <Small color="danger">{error}</Small>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={!selected || submitting}
          variant="contained"
          onClick={handleSubmit}
        >
          {submitting ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddWorkerBadgeModal;
